import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import './FormDialog.css';

interface FormDialogProps {
    open: boolean;
    /** Fechar sem decidir: Esc ou o botão neutro. **Não** no clique fora. */
    onClose: () => void;
    /**
     * A pergunta, **com o nome do que está em jogo dentro dela** — mesma regra
     * do `Dialog`: "Marcar «Vagner Sartori» como perdida?", não "Motivo da
     * perda". Quem lê só a primeira linha já sabe o que vai acontecer.
     */
    titulo: string;
    /**
     * Uma linha sobre o que a decisão provoca. É aqui que mora o texto que
     * antes era uma faixa colorida dentro do corpo — ver o comentário no
     * `LossReasonModal`.
     */
    sub?: ReactNode;
    /** Largura da caixa. O conteúdo decide; o padrão serve para um ou dois campos. */
    largura?: number;
    children: ReactNode;
    /** Texto à esquerda do rodapé: resumo da escolha, contagem, aviso curto. */
    rodapeTexto?: ReactNode;
    /** O que o botão de confirmar faz — **um verbo**, nunca "OK". */
    confirmLabel: string;
    cancelLabel?: string;
    /** O botão neutro, quando ele não é "fechar" (ex.: "Mandar outra"). Sem isto, fecha. */
    onCancel?: () => void;
    onConfirm: () => void;
    /** Falta preencher o que é obrigatório. */
    confirmDisabled?: boolean;
    /** A ação está correndo: trava tudo, inclusive o Esc e o "X". */
    busy?: boolean;
    /** O que o botão diz enquanto corre ("Confirmando…"). */
    busyLabel?: string;
    /** Para o `aria-label` quando o título já diz tudo. */
    ariaLabel?: string;
    /**
     * Clicar no fundo fecha. Desligado por padrão (perder o que se digitou por um
     * clique torto é cruel); ligue só onde o conteúdo sobrevive ao fechar, como a
     * caixinha de sugestões, que guarda o texto até a pessoa reabrir.
     */
    fecharAoClicarFora?: boolean;
}

/**
 * O diálogo que **pede um dado** antes de deixar decidir.
 *
 * Família das mensagens do sistema (proposta de 2026-09-12): o `RowConfirm`
 * confirma na linha, o `Dialog` pergunta e explica — e este existe para o caso
 * que nenhum dos dois cobre, **a decisão que depende de um dado que ainda não
 * existe**: qual foi o motivo da perda, qual produto foi vendido.
 *
 * Por que não estender o `Dialog`: o `Dialog` tirou de propósito o cabeçalho
 * com divisória, o rodapé com fundo e o "X" — numa peça de três linhas, tudo
 * isso é moldura sem função. Com campos a peça cresce e **o corpo passa a
 * rolar**; aí a divisória deixa de ser enfeite e vira o que segura o título no
 * lugar quando o conteúdo desliza por baixo. O precedente certo é o
 * `NovoAgenteModal`, que é modal de fluxo, e é de lá que vêm as medidas.
 *
 * **Clique fora não fecha.** Vale a regra que o `NovoAgenteModal` já segue no
 * passo 2: perder o que se digitou por um clique torto ao lado é cruel. Esc
 * fecha, porque Esc é intenção.
 */
const FormDialog: FC<FormDialogProps> = ({
    open, onClose, titulo, sub, largura, children, rodapeTexto,
    confirmLabel, cancelLabel = 'Cancelar', onCancel, onConfirm,
    confirmDisabled = false, busy = false, busyLabel, ariaLabel, fecharAoClicarFora = false,
}) => {
    const caixaRef = useRef<HTMLDivElement>(null);

    /**
     * Esc fecha **só este diálogo**, que é a camada de cima.
     *
     * `capture: true` + `stopPropagation` pela mesma razão medida no `Dialog`:
     * o `RecordPanel` também escuta Esc no `document` e registrou antes, então
     * sem a captura o painel fechava e o diálogo ficava.
     */
    useEffect(() => {
        if (!open) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || busy) return;
            e.stopPropagation();
            onClose();
        };
        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [open, busy, onClose]);

    /* O foco entra na caixa, não num botão: aqui o primeiro passo é preencher,
       e quem tem `autoFocus` no conteúdo continua ganhando o foco. Sem isto o
       foco fica na página de trás e o leitor de tela não entra no diálogo. */
    useEffect(() => {
        if (!open) return;
        if (!caixaRef.current?.contains(document.activeElement)) caixaRef.current?.focus();
    }, [open]);

    if (!open) return null;

    /* No `body`: a página que abre o diálogo pode estar dentro de container com
       `overflow` ou `transform`, e aí `position: fixed` passa a se medir pelo
       container em vez da janela. Mesmo motivo do `portal` do Select. */
    return createPortal(
        <div
            className="ds-fd-fundo"
            /* `mousedown` e só no próprio fundo: selecionar texto dentro da caixa e
               soltar o botão fora não conta como clique fora. */
            onMouseDown={fecharAoClicarFora ? (e => { if (e.target === e.currentTarget && !busy) onClose(); }) : undefined}
        >
            <div
                ref={caixaRef}
                className="ds-fd"
                style={largura ? { ['--fd-largura' as string]: `${largura}px` } : undefined}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel ?? titulo}
                tabIndex={-1}
            >
                <header className="ds-fd-head">
                    <div className="ds-fd-head-txt">
                        <h2 className="ds-fd-tit" title={titulo}>{titulo}</h2>
                        {sub && <p className="ds-fd-sub">{sub}</p>}
                    </div>
                    <button
                        type="button"
                        className="ds-fd-x"
                        onClick={onClose}
                        aria-label="Fechar"
                        disabled={busy}
                    >
                        <LuX size={16} />
                    </button>
                </header>

                <div className="ds-fd-corpo">{children}</div>

                <div className="ds-fd-pe">
                    {rodapeTexto && <div className="ds-fd-pe-txt">{rodapeTexto}</div>}
                    <button
                        type="button"
                        className="ds-fd-btn"
                        onClick={onCancel ?? onClose}
                        disabled={busy}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className="ds-fd-btn is-primary"
                        onClick={onConfirm}
                        disabled={confirmDisabled || busy}
                    >
                        {busy ? (busyLabel ?? 'Aguarde…') : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default FormDialog;
